// react
import type { MouseEvent } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';

// relative
import type { AggregateType, DateRange, GetAggregateTypeArgs } from '../types';

interface AggregateTypeSelectorProps {
  aggregateOverride: AggregateType | null;
  options: AggregateType[];
  onChange: (value: AggregateType | null) => void;
  getAggregateType: (args: GetAggregateTypeArgs) => AggregateType;
  dateRange: DateRange;
  buttonDataTour?: string;
}

const AggregateTypeSelector = ({
  aggregateOverride,
  options,
  onChange,
  getAggregateType,
  dateRange,
  buttonDataTour,
}: AggregateTypeSelectorProps) => {
  const { t } = useTranslation('view-data');
  const selectedValue = aggregateOverride ?? 'Auto';

  const handleChange = (_: MouseEvent<HTMLElement>, value: string) => {
    if (!value) return;
    onChange(value === 'Auto' ? null : (value as AggregateType));
  };

  return (
    <ToggleButtonGroup
      data-tour={buttonDataTour}
      exclusive
      value={selectedValue}
      onChange={handleChange}
      size="small"
    >
      <ToggleButton value="Auto">{t('aggregate.Auto')}</ToggleButton>
      {options.map((opt) => {
        const disabled =
          getAggregateType({ ...dateRange, requested: opt }) !== opt;
        const label = t(`aggregate.${opt}`) || opt;

        if (!disabled) {
          return (
            <ToggleButton key={opt} value={opt}>
              {label}
            </ToggleButton>
          );
        }

        return (
          <Tooltip
            key={opt}
            title={t(
              `aggregate.tooltip.${opt}`,
              'Du kan ikke vælge denne mulighed med den valgte tidsperiode',
            )}
            placement="top"
          >
            <span>
              <ToggleButton value={opt} disabled>
                {label}
              </ToggleButton>
            </span>
          </Tooltip>
        );
      })}
    </ToggleButtonGroup>
  );
};

export default AggregateTypeSelector;
